import React, { useState, useEffect, useRef } from 'react';
import './AutocompleteInput.css';

/**
 * AutocompleteTextarea - A textarea with autocomplete that remembers previously entered values
 * @param {string} value - Current value
 * @param {function} onChange - Callback when value changes
 * @param {string} storageKey - localStorage key to store/retrieve suggestions
 * @param {string} placeholder - Placeholder text
 * @param {number} maxSuggestions - Maximum number of suggestions to show
 * @param {number} rows - Number of rows for textarea
 */
export default function AutocompleteTextarea({
  value = '',
  onChange,
  storageKey,
  placeholder = '',
  maxSuggestions = 10,
  rows = 4,
  className = '',
  ...textareaProps
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Load suggestions from localStorage
  const loadSuggestions = () => {
    if (!storageKey) return [];
    try {
      const stored = localStorage.getItem(`job_form_autocomplete_${storageKey}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      console.error('Error loading autocomplete suggestions:', e);
    }
    return [];
  };

  // Save a value to localStorage suggestions
  const saveSuggestion = (newValue) => {
    if (!storageKey || !newValue || !newValue.trim()) return;

    try {
      const currentSuggestions = loadSuggestions();
      const trimmedValue = newValue.trim();

      // Remove if already exists and add to beginning
      const filtered = currentSuggestions.filter(
        s => s.toLowerCase() !== trimmedValue.toLowerCase()
      );

      // Add to beginning and limit to maxSuggestions
      const updated = [trimmedValue, ...filtered].slice(0, maxSuggestions);
      localStorage.setItem(`job_form_autocomplete_${storageKey}`, JSON.stringify(updated));
    } catch (e) {
      console.error('Error saving autocomplete suggestion:', e);
    }
  };

  // Filter suggestions based on current input
  const filterSuggestions = (inputValue, allSuggestions) => {
    if (!inputValue || !inputValue.trim()) {
      return allSuggestions.slice(0, maxSuggestions);
    }

    const query = inputValue.trim().toLowerCase();
    return allSuggestions.filter(s => {
      return s.toLowerCase().includes(query);
    }).slice(0, maxSuggestions);
  };

  // Handle input change
  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Show suggestions if there's input
    if (newValue && newValue.trim()) {
      const allSuggestions = loadSuggestions();
      const filtered = filterSuggestions(newValue, allSuggestions);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setHighlightedIndex(-1);
  };

  // Handle input focus
  const handleFocus = () => {
    if (value && value.trim()) {
      const allSuggestions = loadSuggestions();
      const filtered = filterSuggestions(value, allSuggestions);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      const allSuggestions = loadSuggestions();
      setSuggestions(allSuggestions.slice(0, maxSuggestions));
      setShowSuggestions(allSuggestions.length > 0);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    onChange(suggestion);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    if (textareaRef.current) {
      textareaRef.current.blur();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter' && e.ctrlKey && value && value.trim()) {
        // Save current value when Ctrl+Enter is pressed
        e.preventDefault();
        saveSuggestion(value);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[highlightedIndex]);
      } else if (value && value.trim()) {
        // Save current value
        saveSuggestion(value);
        setShowSuggestions(false);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }
  };

  // Handle blur - save value if valid
  const handleBlur = (e) => {
    // Delay to allow click events on suggestions to fire first
    setTimeout(() => {
      if (value && value.trim()) {
        saveSuggestion(value);
      }
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className={`autocomplete-input-container ${className}`}>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        rows={rows}
        className="autocomplete-input"
        {...textareaProps}
      />
      {showSuggestions && suggestions.length > 0 && (
        <ul
          ref={suggestionsRef}
          className="autocomplete-suggestions"
          role="listbox"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion}-${index}`}
              className={`autocomplete-suggestion ${
                index === highlightedIndex ? 'highlighted' : ''
              }`}
              role="option"
              aria-selected={index === highlightedIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectSuggestion(suggestion);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion.length > 60 ? `${suggestion.substring(0, 60)}...` : suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

